import React from 'react';
import PropTypes from 'prop-types';
import AnalyticsContext from './StravaAnalyticsContext';
import TranslationContext from './StravaTranslationContext';
import BranchContext from './StravaBranchContext';
import ErrorLoggingContext from './StravaErrorLoggingContext';
import ExperimentContext from './StravaExperimentContext';
import { LOCALE } from '@strava/constants';
import { devLog } from '@strava/utils';

/**
 * This is mocking strava-container context values that exist in active and nextJS apps.
 * These are used to provide default values for components mounted on unit tests and also
 * for bootstrapping microfrontends in development mode.
 *
 * Read: https://github.com/strava/web/blob/main/microfrontends/README.md#Mocked-Context
 */
const MockedContextProvider = ({
  children,
  analyticsContextValue,
  translationContextValue,
  errorLoggingContextValue,
  branchContextValue,
  experimentContextValue
}) => {
  return (
    <AnalyticsContext.Provider
      value={{ track: () => {}, ...analyticsContextValue }}
    >
      <TranslationContext.Provider
        value={{
          t: (key) => {
            return key;
          },
          getLanguage: () => {
            return LOCALE.enUS;
          },
          ...translationContextValue
        }}
      >
        <ErrorLoggingContext.Provider
          value={{
            logError: (e) => devLog(e),
            ...errorLoggingContextValue
          }}
        >
          <BranchContext.Provider
            value={{
              isProcessing: false,
              createLink: () => {},
              appLink: 'mock_app_link',
              isReady: true,
              ...branchContextValue
            }}
          >
            <ExperimentContext.Provider
              value={{ getExperiment: () => {}, ...experimentContextValue }}
            >
              {children}
            </ExperimentContext.Provider>
          </BranchContext.Provider>
        </ErrorLoggingContext.Provider>
      </TranslationContext.Provider>
    </AnalyticsContext.Provider>
  );
};

MockedContextProvider.defaultProps = {
  analyticsContextValue: {},
  translationContextValue: {},
  errorLoggingContextValue: {}
};

MockedContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  analyticsContextValue: PropTypes.shape(),
  translationContextValue: PropTypes.shape(),
  errorLoggingContextValue: PropTypes.shape()
};

export default MockedContextProvider;
